import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarTree from '../../components/tree/SidebarTree';
import { Breadcrumbs, DateTimeDisplay } from '../../components/ui';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import { getCustomersWithAlarms } from '../../services/bureauManagement/bureauManagement.service';
import type { BureauCustomerTreeNode } from '../../services/bureauManagement/bureauManagement.types';
import type { SiteTreeNode } from '../../services/siteManagement/siteManagement.types';

const POLL_INTERVAL_MS = 60_000;

const AlarmEstateView: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumbs({ currentPageTitle: 'Alarm Estate View' });

  const [nodes, setNodes] = useState<SiteTreeNode[] | null>(null);
  const [unacknowledgedCounts, setUnacknowledgedCounts] = useState<Record<string, number> | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState<number>(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const refresh = useCallback(() => setRefreshTick(v => v + 1), []);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setRefreshTick(v => v + 1);
    }, POLL_INTERVAL_MS);
    return () => { window.clearInterval(timerId); };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getCustomersWithAlarms({ includeAllSites: true });
        if (!mounted) {return;}
        if (!response.success || !response.data) {
          throw new Error(response.error?.message || 'Failed to load alarm estate tree');
        }

        const customers = Array.isArray(response.data.customers) ? response.data.customers : [];
        const treeNodes: SiteTreeNode[] = customers.map((customer: BureauCustomerTreeNode) => ({
          key: customer.key,
          title: customer.title,
          type: customer.type,
          customerId: customer.customerId ?? null,
          children: (customer.children || []).map((site: BureauCustomerTreeNode) => ({
            key: site.key,
            title: site.title,
            type: site.type,
            sirId: site.sirId ?? null,
            siteIdentifier: site.siteIdentifier ?? null,
            siteName: site.siteName ?? null,
            children: site.children || [],
          })),
        }));

        const counts: Record<string, number> = {};
        customers.forEach((customer: BureauCustomerTreeNode) => {
          const customerCount = Number(customer.activeNotAck) || 0;
          if (customerCount > 0) {
            counts[customer.key] = customerCount;
          }
          (customer.children || []).forEach((site: BureauCustomerTreeNode) => {
            const siteCount = Number(site.activeNotAck) || 0;
            if (siteCount > 0) {
              counts[site.key] = siteCount;
            }
          });
        });

        setNodes(treeNodes);
        setUnacknowledgedCounts(Object.keys(counts).length > 0 ? counts : undefined);
        setLastUpdatedAt(new Date().toLocaleTimeString());
      } catch (e) {
        if (!mounted) {return;}
        setError(e instanceof Error ? e.message : 'Failed to load alarm estate tree');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [refreshTick]);

  const totalSites = useMemo(() => {
    if (!Array.isArray(nodes)) {return 0;}
    return nodes.reduce((acc, customer) => acc + (Array.isArray(customer.children) ? customer.children.length : 0), 0);
  }, [nodes]);

  return (
    <main>
      <div className="container mb-2">
        <section className="page">
          <h2 className="heading__title heading__title--monitor">Alarm Estate View</h2>
          <DateTimeDisplay />
        </section>

        <Breadcrumbs items={breadcrumbs} />

        <div className="row mb-1" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="col-8-xs">
            <span>Customers and sites: {totalSites} sites shown (including sites with zero active alarms).</span>
            {lastUpdatedAt && <span style={{ marginLeft: '0.75rem' }}>Last updated: {lastUpdatedAt}</span>}
          </div>
          <div className="col-4-xs" style={{ textAlign: 'right' }}>
            <button type="button" className="btn btn--primary" onClick={refresh}>Refresh</button>
          </div>
        </div>

        <div className="sidebar-tree-wrapper">
          {loading && <div className="tree-loading">Loading alarm estate...</div>}
          {!loading && error && <div className="tree-error">{error}</div>}
          {!loading && !error && nodes && (
            <SidebarTree
              nodes={nodes}
              collapsed={false}
              allowedSiteIdentifiers={undefined}
              allowedTopLevelKeys={undefined}
              allowedChildrenMap={undefined}
              unacknowledgedCounts={unacknowledgedCounts}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              expandedKeys={expandedKeys}
              setExpandedKeys={setExpandedKeys}
              onSelect={(node) => {
                try { setSelectedId(String(node.key)); } catch { /* ignore */ }
                const id = (node as SiteTreeNode).sirId ?? node.key;
                if (String(node.key).startsWith('customer-')) {
                  const customerId = String(node.key).replace('customer-', '');
                  navigate(`/customers/${encodeURIComponent(customerId)}`);
                  return;
                }
                navigate(`/sites/${encodeURIComponent(String(id))}`);
              }}
            />
          )}
        </div>
      </div>
    </main>
  );
};

export default AlarmEstateView;
